import {
  PERSONA_SUGGEST_SCRIPT_DESCRIPTION_MAX_CHARS,
  type ScriptsBroadcastGuidelineRule,
} from "@nexus/contracts";

import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";

import { formatPersonaScriptDocument } from "./persona-script-clipboard.js";
import { getPersonaScriptDisplayTitle } from "./script-display-title.js";

/** Same fallback name the BFF gives a script with no hook or concept. */
const UNTITLED_SCRIPT = "Untitled script";

/**
 * Characters the BFF adds before the script text: the `SOURCE SCRIPT`
 * header, the adapt instruction and the blank line. Mirrors
 * services/api/src/lib/build-broadcast-description.ts; keep the two in step.
 */
const ADAPT_INSTRUCTION_CHARS = 463;
const GUIDELINES_HEADER = "\n\nTENANT GUIDELINES\n";

/** One description per (script, tenant) pair must fit this cap. */
export const BROADCAST_PAIR_DESCRIPTION_MAX_CHARS = PERSONA_SUGGEST_SCRIPT_DESCRIPTION_MAX_CHARS;

/**
 * Length of one script's job description before any guideline, estimated
 * the way the BFF renders it: a script with a full-script body is its title
 * plus that body; otherwise the structured script dump.
 */
export function estimateBroadcastDescriptionLength(script: PersonaScriptDto): number {
  const name = getPersonaScriptDisplayTitle(script, UNTITLED_SCRIPT);
  const body = script.script?.trim();
  const text = body
    ? `${name}\n\n${body}`
    : formatPersonaScriptDocument({ ...script, script: null }, UNTITLED_SCRIPT);
  return ADAPT_INSTRUCTION_CHARS + text.length;
}

/** Characters the guideline block adds: header, "- " per rule, indented continuation lines. */
export function guidelinesBlockLength(texts: readonly string[]): number {
  const lines = texts
    .map((text) => text.trim())
    .filter((text) => text.length > 0)
    .map((text) => `- ${text.replace(/\r?\n/g, "\n  ")}`);
  return lines.length > 0 ? GUIDELINES_HEADER.length + lines.join("\n").length : 0;
}

export interface GuidelinePair {
  scriptId: string;
  tenantId: string;
}

function ruleMatches(rule: ScriptsBroadcastGuidelineRule, pair: GuidelinePair): boolean {
  return (
    (!rule.scriptIds || rule.scriptIds.includes(pair.scriptId)) &&
    (!rule.tenantIds || rule.tenantIds.includes(pair.tenantId))
  );
}

/** Rule texts that reach one pair. Order does not change the length, so it is not sorted. */
export function guidelineTextsForPair(
  rules: readonly ScriptsBroadcastGuidelineRule[],
  pair: GuidelinePair,
): string[] {
  return rules.filter((rule) => ruleMatches(rule, pair)).map((rule) => rule.text);
}

export interface OversizedPair extends GuidelinePair {
  length: number;
}

/**
 * First (script, tenant) pair whose estimated description is over the cap.
 * Scripts without a known length are skipped; the BFF still checks them.
 */
export function findOversizedPair(args: {
  scripts: readonly { id: string; descriptionLength?: number }[];
  tenantIds: readonly string[];
  rules: readonly ScriptsBroadcastGuidelineRule[];
  maxLength?: number;
}): OversizedPair | null {
  const maxLength = args.maxLength ?? BROADCAST_PAIR_DESCRIPTION_MAX_CHARS;
  for (const script of args.scripts) {
    if (script.descriptionLength === undefined) continue;
    for (const tenantId of args.tenantIds) {
      const pair = { scriptId: script.id, tenantId };
      const length =
        script.descriptionLength + guidelinesBlockLength(guidelineTextsForPair(args.rules, pair));
      if (length > maxLength) return { ...pair, length };
    }
  }
  return null;
}

export interface PrunedGuidelineRules {
  rules: ScriptsBroadcastGuidelineRule[];
  /** Rules removed because every id they were scoped to is gone. */
  droppedCount: number;
  /** For each input index, the rule's index in `rules`, or null when it was dropped. */
  indexMap: (number | null)[];
}

function keepPresent(
  ids: string[] | undefined,
  present: ReadonlySet<string>,
): string[] | null | undefined {
  if (!ids) return undefined;
  const kept = ids.filter((id) => present.has(id));
  return kept.length > 0 ? kept : null;
}

/**
 * Fits rules to the scripts and tenants still in the dialog: scoped ids that
 * are gone are removed, and a rule left with none in a scoped list is dropped.
 * Returns the same array when nothing changed.
 */
export function pruneGuidelineRules(
  rules: ScriptsBroadcastGuidelineRule[],
  scriptIds: readonly string[],
  tenantIds: readonly string[],
): PrunedGuidelineRules {
  const scriptIdSet = new Set(scriptIds);
  const tenantSet = new Set(tenantIds);
  let changed = false;
  let droppedCount = 0;
  const next: ScriptsBroadcastGuidelineRule[] = [];
  const indexMap: (number | null)[] = [];
  for (const rule of rules) {
    const scriptIds = keepPresent(rule.scriptIds, scriptIdSet);
    const ruleTenantIds = keepPresent(rule.tenantIds, tenantSet);
    if (scriptIds === null || ruleTenantIds === null) {
      changed = true;
      droppedCount += 1;
      indexMap.push(null);
      continue;
    }
    indexMap.push(next.length);
    const sameScripts = scriptIds?.length === rule.scriptIds?.length;
    const sameTenants = ruleTenantIds?.length === rule.tenantIds?.length;
    if (sameScripts && sameTenants) {
      next.push(rule);
      continue;
    }
    changed = true;
    next.push({
      text: rule.text,
      ...(scriptIds ? { scriptIds } : {}),
      ...(ruleTenantIds ? { tenantIds: ruleTenantIds } : {}),
    });
  }
  return { rules: changed ? next : rules, droppedCount, indexMap };
}
